<template>
  <div class="bg-slate-50">
    <AppBar />

    <!-- Category shortcut strip (sub-nav) -->
    <CategoryStrip
      :categories="categoriesShort"
      :loading="loading"
      @category-click="goCategory"
      @all-click="scrollToAllCategories"
    />

    <main class="bg-slate-50">
      <!-- HERO -->
      <HeroCarousel :images="heroImages" :loading="loading" />

      <!-- DEAL OF THE DAY + 3 promo tiles -->
      <section v-if="loading || dealOfTheDay || sidePromos.length" class="px-4 sm:px-6 md:px-10 lg:px-40 mt-8">
        <header class="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 class="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">{{ $t('home.promotionsTitle') }}</h2>
            <p class="text-sm text-slate-500 mt-0.5">{{ $t('home.promotionsSub') }}</p>
          </div>
        </header>

        <div v-if="loading" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div class="lg:col-span-2 h-72 rounded-2xl bg-white border border-slate-200 animate-pulse" />
          <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
            <div v-for="i in 3" :key="i" class="h-20 rounded-xl bg-white border border-slate-200 animate-pulse" />
          </div>
        </div>
        <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <DealOfTheDayCard
            v-if="dealOfTheDay"
            class="lg:col-span-2"
            :product="dealOfTheDay"
            @click="goProduct"
          />
          <div v-if="sidePromos.length" class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
            <button
              v-for="p in sidePromos" :key="p.id"
              class="group flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 hover:shadow-card transition-shadow text-left"
              @click="goProduct(p)"
            >
              <div class="h-16 w-16 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                <img v-if="firstImage(p)" :src="firstImage(p)" :alt="p.nom" class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" decoding="async" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-slate-900 line-clamp-2">{{ p.nom }}</p>
                <div class="mt-1 flex items-baseline gap-2 flex-wrap">
                  <span class="text-sm font-bold text-red-600">{{ formatCurrency(priceWithReduction(p)) }}</span>
                  <span class="text-xs text-slate-400 line-through">{{ formatCurrency(p.prix) }}</span>
                </div>
              </div>
              <span v-if="reductionPct(p) > 0" class="px-2 py-0.5 rounded-md bg-red-50 text-red-600 text-xs font-bold shrink-0">−{{ Math.round(reductionPct(p)) }}%</span>
            </button>
          </div>
        </div>
      </section>

      <!-- VOOR JOU GESELECTEERD — Selected for you -->
      <ProductRail
        v-if="loading || filteredPopular.length"
        :title="$t('home.selectedFor')"
        :flair="$t('home.selectedForYou')"
        :subtitle="$t('home.selectedSub')"
        :products="filteredPopular"
        :loading="loading"
        :wishlist-ids="wishlistIds"
        :show-wishlist="true"
        @product-click="goProduct"
        @wishlist-toggle="toggleWishlist"
      />

      <!-- CATEGORY TILES — square illustration tiles -->
      <section class="px-4 sm:px-6 md:px-10 lg:px-40 mt-12">
        <header class="mb-4">
          <h2 class="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">{{ $t('home.browseTitle') }}</h2>
          <p class="text-sm text-slate-500 mt-0.5">{{ $t('home.browseSub') }}</p>
        </header>

        <div v-if="loading" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <div v-for="i in 10" :key="i" class="aspect-[4/5] rounded-2xl bg-white border border-slate-200 animate-pulse" />
        </div>
        <div v-else-if="categories.length" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <button
            v-for="cat in featuredCategories" :key="`tile-${cat.id}`"
            class="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-brand-300 hover:shadow-card transition-all flex flex-col text-left"
            @click="goCategory(cat)"
          >
            <div class="relative w-full aspect-[4/3] bg-slate-50 overflow-hidden">
              <img v-if="cat.image" :src="cat.image" :alt="cat.nom" class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" decoding="async" />
              <div v-else class="absolute inset-0 flex items-center justify-center text-slate-300">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16l5-5 4 4 5-5 4 4M21 19a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14z"/></svg>
              </div>
            </div>
            <div class="px-3 py-3">
              <span class="block text-sm sm:text-base font-semibold text-slate-900 line-clamp-1">{{ cat.nom }}</span>
              <span v-if="(cat.category_types || []).length" class="block text-xs text-slate-500 mt-0.5">{{ $t('home.subcategoryCount', { n: (cat.category_types || []).length }) }}</span>
            </div>
          </button>
        </div>
      </section>

      <!-- ALL PROMOTIONS RAIL -->
      <ProductRail
        v-if="loading || filteredPromotions.length"
        :title="$t('home.topDealsTitle')"
        accent="red"
        :subtitle="$t('home.topDealsSub')"
        :products="filteredPromotions"
        :loading="loading"
        :wishlist-ids="wishlistIds"
        :show-wishlist="true"
        @product-click="goProduct"
        @wishlist-toggle="toggleWishlist"
      />

      <!-- EXPIRING SOON RAIL -->
      <ProductRail
        v-if="filteredExpiring.length"
        :title="$t('home.expiringTitle')"
        accent="red"
        :subtitle="$t('home.expiringSub')"
        :products="filteredExpiring"
        :countdown="true"
        :wishlist-ids="wishlistIds"
        :show-wishlist="true"
        @product-click="goProduct"
        @wishlist-toggle="toggleWishlist"
      />

      <!-- NEW ARRIVALS RAIL -->
      <ProductRail
        v-if="loading || filteredNewArrivals.length"
        :title="$t('home.newTitle')"
        :flair="$t('home.newFlair')"
        :subtitle="$t('home.newSub')"
        :products="filteredNewArrivals"
        :loading="loading"
        :wishlist-ids="wishlistIds"
        :show-wishlist="true"
        @product-click="goProduct"
        @wishlist-toggle="toggleWishlist"
      />

      <!-- CATALOG RAIL -->
      <ProductRail
        v-if="loading || filteredNonDiscounted.length"
        :title="$t('home.allTitle')"
        :subtitle="$t('home.allSub')"
        :products="filteredNonDiscounted"
        :loading="loading"
        :wishlist-ids="wishlistIds"
        :show-wishlist="true"
        @product-click="goProduct"
        @wishlist-toggle="toggleWishlist"
      />

      <!-- BROWSE ALL CATEGORIES (accordion) -->
      <section ref="allCategoriesAnchor" class="px-4 sm:px-6 md:px-10 lg:px-40 mt-12">
        <header class="mb-4">
          <h2 class="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">{{ $t('home.discoverTitle') }}</h2>
          <p class="text-sm text-slate-500 mt-0.5">{{ $t('home.discoverSub') }}</p>
        </header>

        <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="i in 6" :key="i" class="rounded-xl bg-white border border-slate-200 h-20 animate-pulse" />
        </div>
        <div v-else-if="categories.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <article
            v-for="cat in categories" :key="`expand-${cat.id}`"
            class="rounded-xl border border-slate-200 bg-white overflow-hidden"
          >
            <button
              class="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
              @click="toggleExpanded(cat.id)"
            >
              <span class="flex items-center gap-3 min-w-0">
                <span class="h-9 w-9 rounded-md bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                  <img v-if="cat.image" :src="cat.image" :alt="cat.nom" class="h-full w-full object-cover" loading="lazy" decoding="async" />
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7h18M3 12h18M3 17h12"/></svg>
                </span>
                <span class="min-w-0">
                  <span class="block font-semibold text-slate-900 truncate">{{ cat.nom }}</span>
                  <span class="block text-xs text-slate-500">{{ $t('home.subcategoryCount', { n: (cat.category_types || []).length }) }}</span>
                </span>
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-slate-400 transition-transform shrink-0"
                :class="expandedCategoryIds.has(cat.id) ? 'rotate-180' : ''"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
            <div v-if="expandedCategoryIds.has(cat.id)" class="px-4 pb-4">
              <div v-if="(cat.category_types || []).length" class="flex flex-wrap gap-2">
                <button
                  v-for="t in cat.category_types" :key="`${cat.id}-${t.id}`"
                  class="h-8 px-3 rounded-full text-xs font-medium bg-slate-100 text-slate-700 hover:bg-brand-50 hover:text-brand-700 border border-transparent hover:border-brand-200 transition-colors"
                  @click="goCategoryType(cat.id, t.nom)"
                >
                  {{ t.nom }}
                </button>
              </div>
              <p v-else class="text-xs text-slate-400">{{ $t('home.noSubcategories') }}</p>
            </div>
          </article>
        </div>
      </section>

      <!-- TRUST BAND -->
      <section class="mt-12 bg-white border-t border-slate-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div class="flex items-start gap-3">
            <span class="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7h13l3 4v6m-2 0a2 2 0 11-4 0 2 2 0 014 0zM7 17a2 2 0 11-4 0 2 2 0 014 0zM3 7v10h2"/></svg>
            </span>
            <div>
              <p class="font-semibold text-slate-900">{{ $t('trust.deliveryTitle') }}</p>
              <p class="text-sm text-slate-500">{{ $t('trust.deliverySub') }}</p>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <span class="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 14l2 2 4-4m5 0a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </span>
            <div>
              <p class="font-semibold text-slate-900">{{ $t('trust.returnsTitle') }}</p>
              <p class="text-sm text-slate-500">{{ $t('trust.returnsSub') }}</p>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <span class="h-10 w-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
            </span>
            <div>
              <p class="font-semibold text-slate-900">{{ $t('trust.reviewsTitle') }}</p>
              <p class="text-sm text-slate-500">{{ $t('trust.reviewsSub') }}</p>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <span class="h-10 w-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"/></svg>
            </span>
            <div>
              <p class="font-semibold text-slate-900">{{ $t('trust.paymentTitle') }}</p>
              <p class="text-sm text-slate-500">{{ $t('trust.paymentSub') }}</p>
            </div>
          </div>
        </div>
      </section>

      <div class="h-8" />
    </main>

    <LanguageDialog v-if="firstVisit" @city-selected="handleCitySelected" />
    <ToastContainer />

    <WishlistPickerDialog
      :open="wishlistPickerOpen"
      :product="wishlistPickerProduct"
      :client-id="clientId"
      @close="wishlistPickerOpen = false"
      @changed="onWishlistChanged"
    />
  </div>
</template>

<script>
import axios from 'axios';
import AppBar from '@/components/AppBar.vue';
import LanguageDialog from '@/components/LanguageDialog.vue';
import HeroCarousel from '@/components/storefront/HeroCarousel.vue';
import CategoryStrip from '@/components/storefront/CategoryStrip.vue';
import ProductRail from '@/components/storefront/ProductRail.vue';
import DealOfTheDayCard from '@/components/storefront/DealOfTheDayCard.vue';
import ToastContainer from '@/components/admin/ui/ToastContainer.vue';
import WishlistPickerDialog from '@/components/storefront/WishlistPickerDialog.vue';
import { toasts } from '@/services/toast';
import i18n from '@/i18n';

const t = (key, params) => i18n.global.t(key, params);

export default {
  name: 'StorefrontHomePage',
  components: { AppBar, LanguageDialog, HeroCarousel, CategoryStrip, ProductRail, DealOfTheDayCard, ToastContainer, WishlistPickerDialog },
  data() {
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      selectedCity: '',
      firstVisit: false,

      loading: true,
      heroImages: [],
      categories: [],
      promotions: [],
      expiringProducts: [],
      popularProducts: [],
      newArrivals: [],
      nonDiscounted: [],
      wishlistIds: [],
      expandedCategoryIds: new Set(),
      wishlistPickerOpen: false,
      wishlistPickerProduct: null,
    };
  },
  computed: {
    isMobile() { return this.windowWidth < 768; },
    clientId() {
      // Read from Vuex first, then fall back to localStorage so the heart
      // works on a hard refresh before the persisted store has rehydrated.
      return this.$store.state.clientId || (typeof localStorage !== 'undefined' ? localStorage.getItem('clientId') : null);
    },

    cityFilter() {
      const city = (this.selectedCity || '').toLowerCase();
      if (!city) return () => true;
      return (p) => {
        if (Array.isArray(p?.villes)) return p.villes.some((v) => String(v).toLowerCase() === city);
        if (p?.ville) return String(p.ville).toLowerCase() === city;
        return true;
      };
    },
    filteredPromotions()    { return this.promotions.filter(this.cityFilter); },
    filteredExpiring()      { return this.expiringProducts.filter(this.cityFilter); },
    filteredPopular()       { return this.popularProducts.filter(this.cityFilter); },
    filteredNewArrivals()   { return this.newArrivals.filter(this.cityFilter); },
    filteredNonDiscounted() { return this.nonDiscounted.filter(this.cityFilter); },

    /**
     * Pick the deal of the day = expiring promo with the soonest end_date,
     * fall back to the most-discounted active promotion.
     */
    dealOfTheDay() {
      if (this.filteredExpiring.length) {
        return [...this.filteredExpiring].sort((a, b) => {
          const ea = new Date(a?.reduction?.end_date || a?.promotion_end || 0).getTime();
          const eb = new Date(b?.reduction?.end_date || b?.promotion_end || 0).getTime();
          return ea - eb;
        })[0];
      }
      if (this.filteredPromotions.length) {
        return [...this.filteredPromotions].sort((a, b) => this.reductionPct(b) - this.reductionPct(a))[0];
      }
      return null;
    },
    sidePromos() {
      // Three other top-discount promotions, excluding the deal-of-the-day.
      const dealId = this.dealOfTheDay?.id;
      return [...this.filteredPromotions]
        .filter((p) => p.id !== dealId)
        .sort((a, b) => this.reductionPct(b) - this.reductionPct(a))
        .slice(0, 3);
    },

    /** Full category list — rendered in the tile grid and the sub-nav strip. */
    featuredCategories() { return this.categories; },
    categoriesShort()    { return this.categories; },
  },
  methods: {
    handleCitySelected(city) { this.selectedCity = city || ''; },
    goProduct(product) {
      if (!product?.id) return;
      this.$router.push({ name: 'ProductDetails', params: { id: product.id } });
    },
    goCategory(cat) {
      this.$router.push({ name: 'ProduitCategory', params: { id: cat.id, name: cat.nom } });
    },
    goCategoryType(catId, typeName) {
      this.$router.push({ name: 'ProduitCategory', params: { id: catId, name: typeName } });
    },
    toggleExpanded(id) {
      const next = new Set(this.expandedCategoryIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      this.expandedCategoryIds = next;
    },
    scrollToAllCategories() {
      const el = this.$refs.allCategoriesAnchor;
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    handleResize() { this.windowWidth = window.innerWidth; },

    firstImage(p) {
      const imgs = p?.images;
      if (!Array.isArray(imgs) || !imgs.length) return null;
      const first = imgs[0];
      return typeof first === 'string' ? first : (first?.image || null);
    },
    reductionPct(p) {
      if (p?.reduction_percentage != null) return Number(p.reduction_percentage) || 0;
      return Number(p?.reduction?.percentage) || 0;
    },
    priceWithReduction(p) {
      if (p?.prix_with_reduction != null) return Number(p.prix_with_reduction) || 0;
      const prix = Number(p?.prix || 0);
      return prix - prix * (this.reductionPct(p) / 100);
    },
    formatCurrency(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${n.toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
    },

    /**
     * Single home aggregator + the image-home banner — two parallel fetches.
     * No N+1: each section reads from one of the arrays returned by the aggregator.
     */
    async fetchAll() {
      this.loading = true;
      const [homeR, heroR] = await Promise.allSettled([
        axios.get('https://scap.ma/api/home/data'),
        axios.get('https://scap.ma/api/image-home'),
      ]);

      if (homeR.status === 'fulfilled') {
        const d = homeR.value.data || {};
        this.categories       = Array.isArray(d.categories) ? d.categories : [];
        this.promotions       = Array.isArray(d.promotions) ? d.promotions : [];
        this.expiringProducts = Array.isArray(d.expiring_soon_products) ? d.expiring_soon_products : [];
        this.popularProducts  = Array.isArray(d.popular_products) ? d.popular_products : [];
        this.newArrivals      = Array.isArray(d.new_arrivals) ? d.new_arrivals : [];
        this.nonDiscounted    = Array.isArray(d.non_discounted_products) ? d.non_discounted_products : [];
      } else {
        toasts.error(t('home.loadFail'), homeR.reason?.response?.data?.message || homeR.reason?.message);
      }

      if (heroR.status === 'fulfilled') {
        const list = Array.isArray(heroR.value.data) ? heroR.value.data : (heroR.value.data?.images || []);
        this.heroImages = list.filter(Boolean);
      }

      this.loading = false;
      if (this.clientId) this.loadWishlist();
    },

    async loadWishlist() {
      const ids = new Set();
      [
        ...this.promotions, ...this.expiringProducts,
        ...this.popularProducts, ...this.newArrivals,
        ...this.nonDiscounted,
      ].forEach((p) => p?.id && ids.add(p.id));
      if (!ids.size) { this.wishlistIds = []; return; }

      try {
        const r = await axios.post(`https://scap.ma/api/clientliste/${this.clientId}/batch`, {
          produit_ids: Array.from(ids),
        });
        const map = r.data?.wishlisted || {};
        this.wishlistIds = Object.entries(map).filter(([, v]) => v).map(([k]) => Number(k));
      } catch {
        this.wishlistIds = [];
      }
    },

    /**
     * Heart click: logged-out → login redirect; logged-in → open the
     * list-picker dialog so the user can choose which list (or create one,
     * or remove the product). The dialog handles the API calls and emits
     * 'changed' back to keep wishlistIds in sync.
     */
    toggleWishlist(product) {
      if (!product?.id) return;
      if (!this.clientId) {
        this.$router.push({ name: 'LogineC', query: { nextUrl: this.$route.fullPath } });
        return;
      }
      this.wishlistPickerProduct = product;
      this.wishlistPickerOpen = true;
    },
    onWishlistChanged({ wishlisted }) {
      const id = this.wishlistPickerProduct?.id;
      if (!id) return;
      if (wishlisted) {
        if (!this.wishlistIds.includes(id)) this.wishlistIds = [...this.wishlistIds, id];
      } else {
        this.wishlistIds = this.wishlistIds.filter((x) => x !== id);
      }
    },
  },
  created() { this.fetchAll(); },
  mounted() {
    if (!localStorage.getItem('visitedBefore')) {
      this.firstVisit = true;
      localStorage.setItem('visitedBefore', 'true');
    }
    window.addEventListener('resize', this.handleResize);

    // Refetch backend data when the user switches language so category
    // names, product names, etc. come back in the new locale.
    this._onLocaleChanged = () => this.fetchAll();
    window.addEventListener('scap:locale-changed', this._onLocaleChanged);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scap:locale-changed', this._onLocaleChanged);
  },
};
</script>
